// src/integrations/tastytrade/client.ts

// Only importing essential types for OAuth flow
import type {
  UserProfile,
  UserProfileResponse,
  TastytradeCustomerAccountResponse,
  TastytradeAccountBalanceResponse,
  TastytradeAccountPositionResponse,
  TastytradeAccount,
  TastytradeAccountBalance,
  TastytradePosition
} from './types';

const tastytradeAuthUrl = 'https://my.tastytrade.com/auth.html';
const tastytradeTokenUrl = 'https://my.tastytrade.com/oauth/token';

// Base URL for Tastytrade API
const tastytradeApiBaseUrl = 'https://api.tastytrade.com';

let tastytradeAccessToken: string | null = null;

function getHeaders() {
  console.log('[tastytradeClient.getHeaders] In-memory token before check:', tastytradeAccessToken ? 'Token Present' : 'Token Missing');
  const tokenFromLocalStorage = localStorage.getItem('tastytrade_access_token');
  console.log('[tastytradeClient.getHeaders] Token from localStorage:', tokenFromLocalStorage ? 'Token Present' : 'Token Missing');
  
  const token = tastytradeAccessToken || tokenFromLocalStorage;
  console.log('[tastytradeClient.getHeaders] Token being used:', token ? 'Token Present' : 'Token MISSING');

  if (!token) {
    console.error('[tastytradeClient.getHeaders] TastyTrade access token is not set. In-memory was:', tastytradeAccessToken ? 'Present' : 'Missing', 'localStorage was:', tokenFromLocalStorage ? 'Present' : 'Missing');
    throw new Error('TastyTrade access token is not set');
  }
  
  return {
    Authorization: 'Bearer ' + token,
    Accept: 'application/json'
  };
}

export function setTastyTradeAccessToken(token: string) {
  tastytradeAccessToken = token;
  localStorage.setItem('tastytrade_access_token', token);
}

export function clearTastyTradeAccessToken() {
  tastytradeAccessToken = null;
  localStorage.removeItem('tastytrade_access_token');
}

export function getTastyTradeAuthUrl() {
  const clientId = import.meta.env.VITE_TASTYTRADE_CLIENT_ID;
  // Default to the backend-registered redirect_uri for local/dev
  const redirectUri = import.meta.env.VITE_TASTYTRADE_REDIRECT_URI || "https://127.0.0.1:8080";
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'customer market_data trading account_viewing account_activity',
  });
  return `${tastytradeAuthUrl}?${params.toString()}`;
}

export async function exchangeTastyTradeCodeForToken(code: string): Promise<string> {
  const clientId = import.meta.env.VITE_TASTYTRADE_CLIENT_ID;
  const clientSecret = import.meta.env.VITE_TASTYTRADE_CLIENT_SECRET;
  // Default to the backend-registered redirect_uri for local/dev
  const redirectUri = import.meta.env.VITE_TASTYTRADE_REDIRECT_URI || "https://127.0.0.1:8080";
  console.log('Exchange TastyTrade code for token:', { clientId, redirectUri });

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
    client_secret: clientSecret
  });

  const response = await fetch(tastytradeTokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`TastyTrade token error ${response.status}: ${text}`);
  }

  const data = await response.json();
  setTastyTradeAccessToken(data.access_token);
  return data.access_token;
}

// Simple function to verify if user is authenticated
export async function verifyAuthentication(): Promise<boolean> {
  try {
    const response = await fetch('https://api.tastytrade.com/customers/me', {
      headers: getHeaders()
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

export async function getTastytradeAccounts(): Promise<TastytradeAccount[]> {
  const response = await fetch(`${tastytradeApiBaseUrl}/customers/me/accounts`, {
    headers: getHeaders()
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch Tastytrade accounts: ${response.status} ${errorText}`);
  }
  const data: TastytradeCustomerAccountResponse = await response.json();
  // Extract the 'account' object from each item
  return data.data.items.map(item => item.account);
}

export async function getTastytradeAccountBalance(accountNumber: string): Promise<TastytradeAccountBalance> {
  if (!accountNumber || accountNumber === 'undefined') {
    console.error('[tastytradeClient] getTastytradeAccountBalance called with invalid accountNumber:', accountNumber);
    throw new Error('Invalid accountNumber provided to getTastytradeAccountBalance.');
  }
  const response = await fetch(`${tastytradeApiBaseUrl}/accounts/${accountNumber}/balances`, {
    headers: getHeaders()
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch Tastytrade account balance for ${accountNumber}: ${response.status} ${errorText}`);
  }
  const data: TastytradeAccountBalanceResponse = await response.json();
  return data.data;
}

export async function getTastytradeAccountPositions(accountNumber: string): Promise<TastytradePosition[]> {
  const response = await fetch(`${tastytradeApiBaseUrl}/accounts/${accountNumber}/positions`, {
    headers: getHeaders()
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch Tastytrade positions for ${accountNumber}: ${response.status} ${errorText}`);
  }
  const data: TastytradeAccountPositionResponse = await response.json();
  return data.data.items;
}

// Export only OAuth-related functionality
const tastytradeClient = { 
  getTastyTradeAuthUrl, 
  exchangeTastyTradeCodeForToken, 
  setTastyTradeAccessToken, 
  clearTastyTradeAccessToken,
  verifyAuthentication,
  getTastytradeAccounts,
  getTastytradeAccountBalance,
  getTastytradeAccountPositions
};

export default tastytradeClient;